import { Component, OnInit } from '@angular/core';
import {
  DataHubStatus,
  MeterDto,
  MeteringPointAssociateMeterDto,
  MeteringPointDto,
  MeteringPointService,
  MeteringPointSubType,
  MeterReadingType,
  MeterService,
  ProductCharacteristicUnitType,
  Quadrant
} from '@app/api/dmc';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { FormApiService } from '@app/core/services/form-api.service';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { meterTableConfig } from '@shared/table-configs/meter/meter-dto.table';
import { removeNullValues } from '@app/core/utils/remove-null-values';
import { filter, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-create-meter-modal',
  imports: [
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    SelectDirective,
    TranslatePipe,
    InputDateDirective,
  ],
  templateUrl: './create-meter-modal.component.html',
  styleUrl: './create-meter-modal.component.css'
})
export class CreateMeterModalComponent implements OnInit, HasUnsavedChanges {
  /** Form **/
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public form = new FormGroup({
    meteringPointIdentification: new FormControl<string | undefined>(undefined, Validators.required),
    meterIdentification: new FormControl<string | null>(null),
    serialNumber: new FormControl<string | null>(null),
    numberOfDigits: new FormControl<string | null>(null),
    unitType: new FormControl<ProductCharacteristicUnitType | null>(ProductCharacteristicUnitType.KWH),
    meterReadingType: new FormControl<MeterReadingType | null>(null),
    meterPointSubType: new FormControl<MeteringPointSubType | null>(null),
    quadrant: new FormControl<Quadrant | null>(null, Validators.required),
    dataHubStatus: new FormControl<DataHubStatus | null>(null),
    validFrom: new FormControl<string | null>(null, Validators.required),
    meterNumber: new FormControl<string | null>(null),
    startMeterStand: new FormControl<number | null>(null),
    createIfNotExists: new FormControl<boolean | null>(null),
    conversionFactorA14: new FormControl<number | null>(null),
    conversionFactorA23: new FormControl<number | null>(null),
    conversionFactorR12: new FormControl<number | null>(null),
    conversionFactorR34: new FormControl<number | null>(null),
  });

  /** Table Config **/
  public tableConfig: TableConfig<MeterDto> = meterTableConfig;

  /** State **/
  public meteringPoint?: MeteringPointDto;
  public meterList: MeterDto[] = [];
  public element?: any;

  /** Enums **/
  public unitType = enumToKeyValue(ProductCharacteristicUnitType, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.meteringpoint.unittype.'
  });
  public meterReadingType = enumToKeyValue(MeterReadingType, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.meteringpoint.meterreadingtype.'
  });
  public quadrant = enumToKeyValue(Quadrant, {omitKeys: ['NotSpecified'], prefixValue: 'enum.quadrant.'});

  constructor(
    private readonly meteringPointService: MeteringPointService,
    private readonly formApiService: FormApiService,
    private readonly meterService: MeterService,
  ) {
  }

  public ngOnInit(): void {
    this.loadMeteringPointAndMeters();

    this.form.get('meterNumber')?.valueChanges.subscribe(value => {
      if (!value) return;

      const meter = this.meterList.find(m => m.meterNumber === value);
      if (!meter) return;

      this.form.patchValue(
        {
          meterIdentification: meter.meterNumber ?? meter.serialNumber ?? null,
          serialNumber: meter.serialNumber ?? null,
          numberOfDigits: meter.numberOfDigits,
          meterReadingType: meter.meterReadingType ?? null,
        },
        { emitEvent: false }
      );
    });
  }

  /**
   * Load metering point if missing → then load meter list.
   * Execution order guaranteed by switchMap().
   */
  private loadMeteringPointAndMeters(): void {
    let meteringPoint$ = this.meteringPoint
      ? of(this.meteringPoint)
      : this.meteringPointService.meteringPointGetByIdentificationGet(
        this.element?.meteringPointIdentification
      );

    meteringPoint$
      .pipe(
        tap(mp => (this.meteringPoint = mp)),
        filter(mp => !!mp?.identification),
        switchMap(mp =>
          this.meterService.meterGetbymeteringpointGet(mp.identification!)
        )
      )
      .subscribe(meters => (this.meterList = meters));
  }

  public submit(): void {
    this.form.get('meteringPointIdentification')?.setValue(this.meteringPoint?.identification);

    this.formApiService.submit<MeteringPointAssociateMeterDto>(
      this.form,
      data => this.meteringPointService.meteringPointAssociateMeterPost(data),
      {
        preprocess: data => ({...removeNullValues(data)}),
        successMessage: 'meteringPoint.meter.create.success',
        closeModal: true,
      }
    );
  }
}
